<template>
  <div
    ref="wrapperRef"
    class="flex flex-col surely-table-wrapper"
    :style="{ gap: `${gap}px` }"
  >
    <div v-if="showSelectionDropdown" class="flex flex-row items-center gap-2">
      <SelectionDropdown
        :number-of-selected="selectedCount"
        :current-page-data="processedDataSource"
        :total-count="totalCount"
        :show-select-all="props.showSelectAll"
        :show-select-certain="false"
        :id-key="attrs['identifier-key'] || 'id'"
        :show-unselect-button="false"
        @select-this-page="handleSelectThisPage"
        @select-all="handleSelectAll"
        @unselect-all="handleUnselectAll"
      />

      <slot name="bulk-actions" />
    </div>

    <!-- Surely Vue Table with Virtual Scrolling -->
    <s-table
      v-bind="mergedTableProps"
      ref="tableRef"
      v-model:columns="draggableColumns"
      :data-source="processedDataSource"
      :pagination="false"
      :scroll="computedScrollConfig"
      :row-selection="rowSelectionConfig"
      @change="handleTableChange"
      @column-drag-end="handleColumnDragEnd"
      @resize-column="handleResizeColumn"
    >
      <template
        v-for="(_, name) in slotsToForward"
        v-slot:[name]="slotData"
        :key="name"
      >
        <slot :name="name" v-bind="slotData" />
      </template>

      <!-- Header cell slot - forwards parent's headerCell or uses default -->
      <template #headerCell="{ column }">
        <slot name="headerCell" :column="column">
          <slot :name="`header-${column.key}`" :column="column">
            <span>{{ column.title }}</span>
          </slot>
        </slot>
      </template>

      <template #bodyCell="{ column, record }">
        <!-- Forward to parent's bodyCell slot if provided -->
        <template v-if="slots.bodyCell">
          <slot name="bodyCell" :column="column" :record="record" />
        </template>

        <!-- Actions column - custom dropdown -->
        <div
          v-else-if="column.key === '_row_actions'"
          class="flex items-center justify-center"
          @click.stop
        >
          <a-dropdown
            v-if="getFilteredActions(record).length > 0"
            placement="bottomRight"
            :trigger="['click']"
          >
            <a-button type="text" size="small" class="action_dropdown_trigger">
              <ThemeImage
                alt="more_icon"
                class="w-5 h-5 min-w-5 flex-shrink-0 cursor-pointer opacity-60"
                :icon="imageNames.MORE_VERTICAL"
              />
            </a-button>

            <template #overlay>
              <a-menu>
                <a-menu-item
                  v-for="action in getFilteredActions(record)"
                  :key="typeof action.label === 'string' ? action.label : (action.icon || 'action')"
                  @click="action.handler(record)"
                >
                  <div class="flex items-center gap-2">
                    <ThemeImage
                      v-if="action.icon"
                      class="size-3"
                      :icon="action.icon"
                      alt="action_option"
                    />
                    {{ typeof action.label === 'function' ? action.label(record) : action.label }}
                  </div>
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </div>

        <!-- Custom component columns -->
        <div v-else-if="isCustomColumn(column)" class="flex items-center">
          <component
            :is="getCustomComponent(column)"
            v-bind="getCustomProps(column, record)"
            :row="record"
            @update="$emit('update', $event)"
            @refresh="$emit('refresh', $event)"
          />
        </div>

        <!-- ProfileIcon cells -->
        <div
          v-else-if="shouldShowProfileIcon(column, record)"
          class="flex items-center gap-2 overflow-hidden"
        >
          <ProfileIcon
            :data="getProfileIconData(column, record)"
            :avatar="column.avatar"
            :view="column.view"
            :param="column.view && !column.avatar ? undefined : (column.avatarParam || column.key)"
          />
          <span
            class="truncate-with-tooltip"
            :title="getCellValue(column, record)"
            >{{ getCellValue(column, record) }}</span
          >
        </div>

        <!-- Prefix image cells -->
        <div
          v-else-if="hasPrefixImage(column, record)"
          class="flex items-center gap-2 overflow-hidden"
        >
          <img
            v-if="isExternalUrl(getPrefixImage(column, record))"
            :src="getPrefixImage(column, record)"
            alt="prefix"
            class="size-6 flex-shrink-0 object-cover"
            :style="`border-radius: ${attrs['prefix-border-radius'] || '4px'}`"
          />
          <ThemeImage
            v-else
            :icon="getPrefixImage(column, record)"
            alt="prefix"
            class="size-4 flex-shrink-0"
            style="border-radius: 50%"
          />
          <span
            class="truncate-with-tooltip"
            :title="getCellValue(column, record)"
            >{{ getCellValue(column, record) }}</span
          >
        </div>

        <!-- Array values - tags or avatar groups -->
        <div v-else-if="isArrayValue(column, record)">
          <AvatarStack
            v-if="column.multiType === 'avatarGroup'"
            :items="getArrayValue(column, record)"
            :max-visible="3"
            :size="28"
            :font-size="12"
            :border-radius="50"
            :overlap="8"
          />
          <div
            v-else
            class="flex gap-1 items-center overflow-hidden"
            :class="{ 'flex-wrap': props.autoHeight }"
            :title="getArrayValue(column, record).join(', ')"
          >
            <template v-if="props.autoHeight">
              <div
                v-for="(item, idx) in getArrayValue(column, record)"
                :key="idx"
                class="tag_css"
              >
                {{ item }}
              </div>
            </template>
            <template v-else>
              <div
                v-for="(item, idx) in getArrayValue(column, record).slice(
                  0,
                  maxVisibleTags,
                )"
                :key="idx"
                class="tag_css flex-shrink-0"
              >
                {{ item }}
              </div>
              <span
                v-if="getArrayValue(column, record).length > maxVisibleTags"
                class="text-xs text-gray-500 flex-shrink-0"
              >
                +{{ getArrayValue(column, record).length - maxVisibleTags }}
              </span>
            </template>
          </div>
        </div>

        <!-- HTML content -->
        <span
          v-else-if="column.html"
          class="truncate-with-tooltip"
          :title="getCellValue(column, record)"
          v-html="getCellValue(column, record)"
        ></span>

        <!-- URL/Domain cells - clickable links -->
        <a
          v-else-if="isLinkableColumn(column, record)"
          :href="formatHref(getCellValue(column, record))"
          target="_blank"
          rel="noopener noreferrer"
          class="truncate-with-tooltip text-inherit no-underline hover:underline cursor-pointer"
          :title="getCellValue(column, record)"
          @click.stop
          >{{ getCellValue(column, record) }}</a
        >

        <!-- Regular text cells - use native title for tooltip on truncation -->
        <span
          v-else
          class="truncate-with-tooltip"
          :title="getCellValue(column, record)"
          >{{ getCellValue(column, record) }}</span
        >
      </template>
    </s-table>

    <PaginationHandler
      v-if="paginationConfig"
      :pagination="paginationConfig"
      :allow-change-page-size="attrs['show-page-size'] !== false"
      :per-page-options="pageSizeOptions"
      :show-go-to-page="attrs.pagination?.showGoToPage || false"
      @update:pagination="handlePaginationUpdate"
    />
  </div>
</template>

<script setup>
import { computed, useAttrs, useSlots, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { imageNames } from "@/src/constants/imageConstants.js";
import { TABLE_COLUMN_WIDTHS } from "@/src/constants/tableConstants.js";
import { linkableAttributeTypes } from "@/src/constants/crmConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import AvatarStack from "@/src/components/common/AvatarStack.vue";
import SelectionDropdown from "@/src/components/SelectionDropdown.vue";
import PaginationHandler from "@/src/components/custom_table_components/PaginationHandler.vue";
import { parseLastActivity, formatHref } from "@/src/utils/utils.js";
import { STable, setLicenseKey } from "@surely-vue/table";
import "@surely-vue/table/dist/index.css";

// Set Surely Vue Table license key from window config (injected from Rails)
setLicenseKey(window.SURELY_TABLE_CONFIG?.licenseKey || "");

const props = defineProps({
  autoHeight: {
    type: Boolean,
    default: true,
  },
  showSelectAll: {
    type: Boolean,
    default: true,
  },
  initialSort: {
    type: Object,
    default: () => ({}),
  },
  gap: {
    type: Number,
    default: 16,
  },
  columns: {
    type: Array,
    default: null,
  },
  scroll: {
    type: Object,
    default: null,
  },
  tableProps: {
    type: Object,
    default: () => ({}),
  },
  fitToContainerWidth: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:pagination",
  "update:selection",
  "update:columns",
  "update:column-width",
  "update:sort",
  "update",
  "refresh",
  "action-completed",
]);
const attrs = useAttrs();
const tableRef = ref(null);
const wrapperRef = ref(null);
const containerWidth = ref(0);
let containerResizeObserver = null;
const slots = useSlots();

const maxVisibleTags = 2;

// Filter out headerCell and bodyCell from slots to forward (we handle them separately)
const slotsToForward = computed(() => {
  const result = {};
  for (const name in slots) {
    if (name !== "headerCell" && name !== "bodyCell") {
      result[name] = slots[name];
    }
  }
  return result;
});

const localColumnOrder = ref([]);
const draggableColumns = ref([]);

watch(
  () => attrs.headers,
  (newHeaders) => {
    if (
      newHeaders &&
      (!localColumnOrder.value || localColumnOrder.value.length === 0)
    ) {
      localColumnOrder.value = newHeaders.map((h) => h.key);
    }
  },
  { immediate: true, deep: true },
);

// Cache for column objects to avoid re-creating on every render
const columnCache = new Map();

// Fixed column width constants
const ACTIONS_COLUMN_WIDTH = 60;

const actionsColumn = {
  title: "",
  key: "_row_actions",
  dataIndex: "_row_actions",
  align: "center",
  fixed: "right",
  width: ACTIONS_COLUMN_WIDTH,
  minWidth: ACTIONS_COLUMN_WIDTH,
  maxWidth: ACTIONS_COLUMN_WIDTH,
  drag: false,
  resizable: false,
};

// Helper to parse fixWidth - defined once outside computed
const parseFixWidth = (fixWidth) => {
  if (!fixWidth) return null;
  if (typeof fixWidth === "number") return fixWidth;
  if (typeof fixWidth === "string") {
    const parsed = parseInt(fixWidth, 10);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

// Helper to calculate column width

const calculateColumnWidth = (header, defaultWidth, useHeaderWidth = false) => {
  const fixWidthValue = parseFixWidth(header.fixWidth);
  if (fixWidthValue) return fixWidthValue;

  // Always respect explicit width values >= 50 (pixel values)
  if (header.width) {
    if (typeof header.width === "number" && header.width >= 50) {
      return header.width;
    }

    if (typeof header.width === "string") {
      const parsed = parseInt(header.width, 10);
      if (!isNaN(parsed) && parsed >= 50) {
        return parsed;
      }
    }
  }

  return defaultWidth;
};

const getHeaderTooltipConfig = (header) => {
  if (header.ellipsis === false) {
    return {
      showSorterTooltip: false,
      customHeaderCell: () => ({ title: "" }),
    };
  }
  return {};
};

const getSortOrder = (columnKey) => {
  if (!props.initialSort || Object.keys(props.initialSort).length === 0) {
    return false;
  }

  const sortEntry = Object.values(props.initialSort).find(
    (sort) => sort.field === columnKey,
  );
  if (sortEntry) {
    return sortEntry.order === "asc" ? "ascend" : "descend";
  }

  return false;
};

const getOrCreateColumn = (header, defaultWidth, useHeaderWidth = false) => {
  const columnKey = header.key || header.dataIndex;
  const columnTitle = header.label || header.title;
  const sortOrder = getSortOrder(columnKey);

  const cacheKey = `${columnKey}_${header.fixWidth}_${header.width}_${header.fixed}_${useHeaderWidth}_${sortOrder}`;

  if (columnCache.has(cacheKey)) {
    return columnCache.get(cacheKey);
  }

  const calculatedWidth = calculateColumnWidth(
    header,
    defaultWidth,
    useHeaderWidth,
  );
  const fixWidthValue = parseFixWidth(header.fixWidth);

  const column = {
    title: columnTitle,
    dataIndex: columnKey,
    key: columnKey,
    width: calculatedWidth,
    minWidth: fixWidthValue || 60,
    align: header.align || "left",
    sorter: header.sortable || header.sorter || false,
    sortOrder: sortOrder,
    html: header.html || false,
    avatar: header.avatar,
    view: header.view,
    avatarDataKey: header.avatarDataKey,
    avatarParam: header.avatarParam,
    prefix: header.prefix,
    multiType: header.multiType,
    ellipsis: header.ellipsis !== false,
    fixed: header.fixed,
    resizable: header.resizable !== false && !header.fixed,
    autoHeight: props.autoHeight && header.autoHeight !== false,
    attributeType: header.attributeType || null,

    ...(header.fixed || columnKey === "_row_actions" ? { drag: false } : {}),

    ...(fixWidthValue ? { maxWidth: fixWidthValue, resizable: false } : {}),

    ...getHeaderTooltipConfig(header),
  };

  columnCache.set(cacheKey, column);
  return column;
};

const hasFixedColumns = computed(() => {
  const headers = attrs.headers || [];
  return headers.some(
    (header) => header.fixed === "left" || header.fixed === "right",
  );
});

const xVirtualEnabled = computed(() => {
  if (attrs["force-x-virtual"]) return true;

  return !hasFixedColumns.value;
});

const mergedTableProps = computed(() => ({
  rowKey: (record) => record.key || record.id,
  virtual: true,
  xVirtual: xVirtualEnabled.value,
  columnWidth: TABLE_COLUMN_WIDTHS.DEFAULT,
  maxHeight: attrs['max-height'] || attrs.height || 500,
  customRow: attrs['custom-row'],
  columnDrag: true,
  ...props.tableProps,
}));

const processedColumns = computed(() => {
  const headers = attrs.headers || [];
  if (headers.length === 0) return [];

  const defaultColumnWidth = TABLE_COLUMN_WIDTHS.DEFAULT;

  const useHeaderWidth = hasFixedColumns.value && !attrs["force-x-virtual"];

  let orderedHeaders = headers;

  if (localColumnOrder.value.length > 0) {
    const orderSet = new Set(localColumnOrder.value);
    const headersMap = new Map(headers.map((h) => [h.key, h]));

    const ordered = [];
    for (const key of localColumnOrder.value) {
      const header = headersMap.get(key);
      if (header) ordered.push(header);
    }

    const remaining = [];
    for (const h of headers) {
      if (!orderSet.has(h.key)) remaining.push(h);
    }

    orderedHeaders = ordered.concat(remaining);
  }

  const columns = orderedHeaders.map((header) =>
    getOrCreateColumn(header, defaultColumnWidth, useHeaderWidth),
  );

  if (attrs["show-actions-column"] && attrs.actions?.length > 0) {
    columns.push(actionsColumn);
  }

  if (props.fitToContainerWidth && containerWidth.value > 0 && columns.length > 0) {
    const checkboxAdjust = attrs["row-selection"]
      ? TABLE_COLUMN_WIDTHS.CHECKBOX
      : 0;
    const colsTotal = columns.reduce(
      (sum, col) => sum + (col.width || defaultColumnWidth),
      0,
    );
    const availableForCols = containerWidth.value - checkboxAdjust;
    if (colsTotal > 0 && colsTotal < availableForCols) {
      const scale = availableForCols / colsTotal;
      return columns.map((col) => ({
        ...col,
        width: Math.floor((col.width || defaultColumnWidth) * scale),
      }));
    }
  }

  return columns;
});

watch(
  () => props.columns ? props.columns : processedColumns.value,
  (newColumns) => {
    if (newColumns?.length > 0) {
      draggableColumns.value = [...newColumns];
    }
  },
  { immediate: true, deep: true },
);

// Handle column drag end event from Surely Table
const handleColumnDragEnd = ({ column, targetColumn, dir }) => {
  if (!column || !targetColumn || column.key === targetColumn.key) return;

  // Build current order excluding actions in single pass
  const currentOrder = [];
  for (const col of draggableColumns.value) {
    if (col.key !== "_row_actions") currentOrder.push(col.key);
  }

  const fromIndex = currentOrder.indexOf(column.key);
  const toIndex = currentOrder.indexOf(targetColumn.key);
  if (fromIndex === -1 || toIndex === -1) return;

  // Compute new order
  const newOrder = [...currentOrder];
  newOrder.splice(fromIndex, 1);

  // Insert position based on drag direction
  const insertIndex =
    fromIndex < toIndex
      ? dir === "right"
        ? toIndex
        : toIndex - 1
      : dir === "right"
        ? toIndex + 1
        : toIndex;

  newOrder.splice(insertIndex, 0, column.key);
  localColumnOrder.value = newOrder;

  emit("update:columns", {
    type: "reorder",
    columnKeys: newOrder,
  });
};

const computedScrollConfig = computed(() => {
  if (props.scroll) return props.scroll;

  if (attrs["disable-horizontal-scroll"]) {
    const onlyVertical = {};
    if (attrs.height || attrs["max-height"]) {
      onlyVertical.y = attrs["max-height"] || attrs.height;
    }
    return onlyVertical;
  }

  const config = {};

  // Calculate total width from draggableColumns so it tracks live resize-drag
  // updates. Using processedColumns here keeps config.x stuck at the original
  // total during a drag, which causes Surely Vue to stretch the rightmost
  // (actions) column to absorb the freed space.
  const columns = draggableColumns.value.length
    ? draggableColumns.value
    : processedColumns.value;
  let totalWidth = 0;
  for (const col of columns) {
    totalWidth += col.width || TABLE_COLUMN_WIDTHS.DEFAULT;
  }

  // Add selection column width if row selection is enabled
  if (attrs["row-selection"]) {
    totalWidth += TABLE_COLUMN_WIDTHS.CHECKBOX;
  }

  if (props.fitToContainerWidth) {
    config.x =
      containerWidth.value > 0
        ? Math.max(totalWidth, containerWidth.value)
        : totalWidth;
    if (attrs.height || attrs["max-height"]) {
      config.y = attrs["max-height"] || attrs.height;
    }
    return config;
  }

  config.x = totalWidth > 800 ? totalWidth : 800;

  if (attrs.height || attrs["max-height"]) {
    config.y = attrs["max-height"] || attrs.height;
  }

  return config;
});

const processedDataSource = computed(() => {
  const data = attrs.data || [];
  const identifierKey = attrs["identifier-key"] || "id";
  return data.map((item) => ({
    ...item,
    key: item[identifierKey] || item.id,
  }));
});

const pageSizeOptions = computed(() => {
  return attrs["per-page-options"] || [10, 20, 50, 100];
});

const paginationConfig = computed(() => {
  if (!attrs.pagination) return null;

  return {
    current: attrs.pagination.current || 1,
    pageSize: attrs.pagination.pageSize || 10,
    total: attrs.pagination.total || 0,
    showCount: attrs.pagination.showCount !== false,
    ...(attrs.pagination.maxDataLimit !== undefined && {
      maxDataLimit: attrs.pagination.maxDataLimit,
    }),
    ...(attrs.pagination.showFirstLastPage && { showFirstLastPage: true }),
    ...(attrs.pagination.deepPaginationThreshold && {
      deepPaginationThreshold: attrs.pagination.deepPaginationThreshold,
    }),
  };
});

const showSelectionDropdown = computed(() => {
  return !!attrs["row-selection"] && selectedCount.value > 0;
});

const internalSelectedRowKeys = ref([]);
const internalSelectAll = ref(false);

const externalSelection = computed(() => {
  const sel = (attrs && attrs.selection) || {};
  return {
    selected_ids: Array.isArray(sel.selected_ids) ? sel.selected_ids : [],
    unselected_ids: Array.isArray(sel.unselected_ids) ? sel.unselected_ids : [],
    select_all: !!sel.select_all,
  };
});

const selectedCount = computed(() => {
  const sel = externalSelection.value;
  if (sel.select_all) {
    const unselected = Array.isArray(sel.unselected_ids)
      ? sel.unselected_ids.length
      : 0;
    return Math.max(0, (totalCount.value || 0) - unselected);
  }
  return Array.isArray(sel.selected_ids) ? sel.selected_ids.length : 0;
});

const totalCount = computed(() => {
  return attrs.pagination?.total || processedDataSource.value.length || 0;
});

const selectedRows = computed(() => {
  return processedDataSource.value.filter((row) =>
    internalSelectedRowKeys.value.includes(row.key || row.id),
  );
});

const selectionData = computed(() => {
  return {
    selectedRowKeys: internalSelectedRowKeys.value,
    selectedRows: selectedRows.value,
    selectedCount: selectedCount.value,
    totalCount: totalCount.value,
  };
});

const clearSelection = () => {
  internalSelectedRowKeys.value = [];
  internalSelectAll.value = false;
  emit("update:selection", {
    selectedRowKeys: [],
    selectedRows: [],
  });
};

const triggerRefresh = () => {
  emit("refresh");
};

const rowSelectionConfig = computed(() => {
  if (!attrs["row-selection"]) return null;

  return {
    type: "checkbox",
    selectedRowKeys: internalSelectedRowKeys.value,
    onChange: (selectedRowKeys, selectedRows) => {
      internalSelectedRowKeys.value = selectedRowKeys;

      const currentPageIds = processedDataSource.value
        .map((row) => row.key || row.id)
        .filter(Boolean);

      const allPageSelected =
        currentPageIds.length > 0 &&
        currentPageIds.every((id) => selectedRowKeys.includes(id));

      internalSelectAll.value = allPageSelected;

      const parentSel = externalSelection.value;
      let newSelection;
      if (parentSel.select_all) {
        const pageUnselected = currentPageIds.filter(
          (id) => !selectedRowKeys.includes(id),
        );
        const remainingUnselected = parentSel.unselected_ids.filter(
          (id) => !currentPageIds.includes(id),
        );
        const allUnselected = [...new Set([...remainingUnselected, ...pageUnselected])];
        newSelection = allUnselected.length > 0
          ? { select_all: false, selected_ids: selectedRowKeys, unselected_ids: [] }
          : { select_all: true, selected_ids: [], unselected_ids: [] };
      } else {
        const remainingSelected = (parentSel.selected_ids || []).filter(
          (id) => !currentPageIds.includes(id),
        );
        newSelection = {
          select_all: false,
          unselected_ids: [],
          selected_ids: [
            ...new Set([...remainingSelected, ...selectedRowKeys]),
          ],
        };
      }
      emit("update:selection", newSelection);
    },
  };
});

const handleSelectThisPage = () => {
  const currentPageData = processedDataSource.value;
  const pageIds = currentPageData
    .map((row) => row.key || row.id)
    .filter(Boolean);

  const parentSel = externalSelection.value;
  if (parentSel.select_all) {
    const updatedUnselected = parentSel.unselected_ids.filter(
      (id) => !pageIds.includes(id),
    );
    emit("update:selection", {
      select_all: true,
      selected_ids: [],
      unselected_ids: updatedUnselected,
    });
  } else {
    const updatedSelected = [
      ...new Set([...(parentSel.selected_ids || []), ...pageIds]),
    ];
    emit("update:selection", {
      select_all: false,
      unselected_ids: [],
      selected_ids: updatedSelected,
    });
  }

  internalSelectedRowKeys.value = pageIds;
};

const handleSelectAll = () => {
  internalSelectAll.value = true;
  internalSelectedRowKeys.value = processedDataSource.value
    .map((row) => row.key || row.id)
    .filter(Boolean);
  emit("update:selection", {
    select_all: true,
    selected_ids: [],
    unselected_ids: [],
  });
};

const handleUnselectAll = () => {
  internalSelectedRowKeys.value = [];
  internalSelectAll.value = false;
  emit("update:selection", {
    select_all: false,
    selected_ids: [],
    unselected_ids: [],
  });
};

const isCustomColumn = (column) => {
  return (
    attrs["custom-column-render"] && attrs["custom-column-render"][column.key]
  );
};

const getCustomComponent = (column) => {
  const customRender = attrs["custom-column-render"][column.key];
  return customRender.component || customRender;
};

const getCustomProps = (column, record) => {
  const customRender = attrs["custom-column-render"][column.key];
  const defaultProps = { value: record[column.key] };

  if (!customRender?.props) {
    return defaultProps;
  }

  return typeof customRender.props === "function"
    ? customRender.props(record)
    : defaultProps;
};

const shouldShowProfileIcon = (column, record) => {
  if (linkableAttributeTypes.has(column.attributeType)) return false;
  const value = record[column.key];
  return (
    (column.key === "profile_pic" ||
      column.key === "logo_url" ||
      column.avatar ||
      column.view) &&
    value &&
    !Array.isArray(value)
  );
};

const store = useStore();
const storeUsers = computed(() => store.getters["users/users"] || []);

const getProfileIconData = (column, record) => {
  if (column.avatarDataKey) return record[column.avatarDataKey];
  if (column.view) {
    const displayName = record[column.avatarParam || column.key];
    if (displayName && typeof displayName === "string") {
      const lowerName = displayName.toLowerCase();
      const found = storeUsers.value.find(
        (u) =>
          u.name?.toLowerCase() === lowerName ||
          u.email?.toLowerCase() === lowerName,
      );
      if (found) return found;
      const keyField = column.avatarParam || column.key;
      return {
        name: displayName,
        [keyField]: displayName,
        id: record.associated_entity_id || record.entity_id || undefined,
      };
    }
  }
  return record;
};

const hasPrefixImage = (column, record) => {
  return column.prefix && record[column.prefix] && record[column.key];
};

const getPrefixImage = (column, record) => {
  return record[column.prefix];
};

const isExternalUrl = (value) => {
  if (!value || typeof value !== "string") return false;
  return value.startsWith("http://") || value.startsWith("https://");
};

const isLinkableColumn = (column, record) => {
  const value = getCellValue(column, record);
  if (!value || typeof value !== "string" || value === "--") return false;
  return linkableAttributeTypes.has(column.attributeType);
};

const isArrayValue = (column, record) => {
  const value = record[column.key];
  return Array.isArray(value) && value.length > 0;
};

const getArrayValue = (column, record) => {
  const value = record[column.key];
  return Array.isArray(value) ? value : [];
};

const getCellValue = (column, record) => {
  const value = record[column.key];
  const emptyCellValue = attrs["empty-cell-value"] || "--";
  if (column.key === "add_property") {
    return "";
  }

  if (value === "" || value === null || value === undefined) {
    return emptyCellValue;
  }

  if (Array.isArray(value)) {
    return emptyCellValue;
  }

  if (column.key === "last_activity") {
    const parsed = parseLastActivity(value);
    return parsed || emptyCellValue;
  }

  return value?.text || value;
};

const getFilteredActions = (record) => {
  if (!attrs.actions || !Array.isArray(attrs.actions)) return [];

  return attrs.actions.filter((action) => {
    if (typeof action.condition === "function") {
      return action.condition(record);
    }
    return true;
  });
};

const handleTableChange = (pagination, filters, sorter) => {
  if (sorter && Object.keys(sorter).length > 0) {
    emit("update:sort", sorter);
  }

  if (pagination) {
    emit("update:pagination", {
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
    });
  }
};

const handlePaginationUpdate = (updatedPagination) => {
  emit("update:pagination", updatedPagination);
};

let columnDragRafId = null;
let currentDragX = 0;
let isColumnDragging = false;
let headerMouseDownX = 0;

const getHScrollViewport = () =>
  tableRef.value?.$el?.querySelector(
    ".surely-table-horizontal-scroll-viewport",
  );

const stopDragScroll = () => {
  if (columnDragRafId) {
    cancelAnimationFrame(columnDragRafId);
    columnDragRafId = null;
  }
};

const dragScrollLoop = () => {
  columnDragRafId = requestAnimationFrame(dragScrollLoop);
  if (!currentDragX || !isColumnDragging) return;

  const scrollEl = getHScrollViewport();
  if (!scrollEl) return;

  const tableEl = tableRef.value?.$el;
  if (!tableEl) return;

  const rect = tableEl.getBoundingClientRect();

  const threshold = 180;
  const maxSpeed = 28;

  const maxScrollRight = scrollEl.scrollWidth - scrollEl.clientWidth;

  if (currentDragX > rect.right - threshold) {
    if (scrollEl.scrollLeft < maxScrollRight - 1) {
      const d = Math.min(currentDragX - (rect.right - threshold), threshold);
      scrollEl.scrollLeft += Math.min(maxSpeed, Math.max(2, (d / threshold) * maxSpeed));
    }
  } else if (currentDragX < rect.left + threshold) {
    if (scrollEl.scrollLeft > 1) {
      const d = Math.min((rect.left + threshold) - currentDragX, threshold);
      scrollEl.scrollLeft = Math.max(
        0,
        scrollEl.scrollLeft - Math.min(maxSpeed, Math.max(2, (d / threshold) * maxSpeed)),
      );
    }
  }
};

const onColumnDragMouseMove = (event) => {
  currentDragX = event.clientX;
  if (!isColumnDragging) {
    if (Math.abs(event.clientX - headerMouseDownX) > 4) {
      isColumnDragging = true;
      if (!columnDragRafId) {
        columnDragRafId = requestAnimationFrame(dragScrollLoop);
      }
    }
  }
};

const onColumnDragMouseUp = () => {
  isColumnDragging = false;
  currentDragX = 0;
  stopDragScroll();
  document.removeEventListener("mousemove", onColumnDragMouseMove);
  document.removeEventListener("mouseup", onColumnDragMouseUp);
};

const onTableMouseDown = (event) => {
  if (!event.target?.closest(".surely-table-header-cell")) return;
  const tableEl = tableRef.value?.$el;
  if (!tableEl?.contains(event.target)) return;

  headerMouseDownX = event.clientX;
  isColumnDragging = false;
  currentDragX = 0;
  document.addEventListener("mousemove", onColumnDragMouseMove);
  document.addEventListener("mouseup", onColumnDragMouseUp);
};

onMounted(() => {
  document.addEventListener("mousedown", onTableMouseDown);

  if (props.fitToContainerWidth && wrapperRef.value) {
    const updateContainerWidth = () => {
      if (wrapperRef.value) {
        containerWidth.value = wrapperRef.value.clientWidth;
      }
    };
    updateContainerWidth();
    containerResizeObserver = new ResizeObserver(updateContainerWidth);
    containerResizeObserver.observe(wrapperRef.value);
  }
});

onBeforeUnmount(() => {
  // Clear selection state
  internalSelectedRowKeys.value = [];

  // Clear column order
  localColumnOrder.value = [];

  if (containerResizeObserver) {
    containerResizeObserver.disconnect();
    containerResizeObserver = null;
  }

  document.removeEventListener("mousedown", onTableMouseDown);
  document.removeEventListener("mousemove", onColumnDragMouseMove);
  document.removeEventListener("mouseup", onColumnDragMouseUp);
  stopDragScroll();
});

const scrollTo = (position, behavior = "auto") => {
  if (tableRef.value?.scrollTo) {
    tableRef.value.scrollTo(position, behavior);
  }
};

const getViewport = () => {
  return tableRef.value?.$el?.querySelector(
    ".surely-table-vertical-scroll-viewport",
  );
};

const getScrollTop = () => {
  return getViewport()?.scrollTop || 0;
};

const scrollToRight = (behavior = "smooth") => {
  if (tableRef.value?.scrollTo) {
    // Get the last column index to scroll to the end
    const lastColumnIndex = draggableColumns.value.length - 1;
    if (lastColumnIndex >= 0) {
      tableRef.value.scrollTo({ columnIndex: lastColumnIndex }, behavior);
    }
  }
};

defineExpose({
  clearSelection,
  triggerRefresh,
  selectedRows,
  selectionData,
  selectedCount,
  getSelectedRowKeys: () => internalSelectedRowKeys.value,
  scrollTo,
  scrollToRight,
  getScrollTop,
  getViewport,
  tableRef,
});

const computeInternalSelectionFromExternal = () => {
  const currentPageIds = processedDataSource.value
    .map((row) => row.key || row.id)
    .filter(Boolean);
  const sel = externalSelection.value;
  if (sel.select_all) {
    internalSelectedRowKeys.value = currentPageIds.filter(
      (id) => !sel.unselected_ids.includes(id),
    );
    internalSelectAll.value =
      internalSelectedRowKeys.value.length === currentPageIds.length &&
      currentPageIds.length > 0;
  } else {
    internalSelectedRowKeys.value = currentPageIds.filter((id) =>
      (sel.selected_ids || []).includes(id),
    );
    internalSelectAll.value =
      internalSelectedRowKeys.value.length === currentPageIds.length &&
      currentPageIds.length > 0;
  }
};

watch(
  () => props.initialSort,
  () => {
    columnCache.clear();
  },
  { deep: true },
);

watch(
  () => [processedDataSource.value, externalSelection.value],
  () => {
    computeInternalSelectionFromExternal();
  },
  { deep: true, immediate: true },
);

const handleResizeColumn = (width, column, action) => {
  if (!column?.key) return;
  if (column.key === "_row_actions" || column.key === "add_property") return;

  // Track width on every move so computedScrollConfig (which reads
  // draggableColumns) shrinks in step with the dragged column. Without this,
  // config.x stays at the original total and Surely Vue redistributes the
  // freed space into the rightmost (actions) column during the drag.
  const target = draggableColumns.value.find((c) => c.key === column.key);
  if (target) {
    target.width = width;
  }

  if (action !== "end") return;

  emit("update:column-width", { key: column.key, width });
};
</script>

<style scoped>
.action_dropdown_trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px !important;
  border: none !important;
  box-shadow: none !important;
}

.action_dropdown_trigger:hover {
  background-color: var(--white-opacity-4) !important;
}

/* Performance-optimized truncation with native title tooltip */
.truncate-with-tooltip {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  user-select: text;
  cursor: text;
}

.quick_action_item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  cursor: pointer;
}

.action_dropdown_trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px !important;
  border: none !important;
  box-shadow: none !important;
}

.action_dropdown_trigger:hover {
  background-color: var(--white-opacity-4) !important;
}

/* Show horizontal scrollbar on table hover */
:deep(.surely-table-horizontal-scroll-hidden) {
  opacity: 0 !important;
  visibility: hidden !important;
  transition:
    opacity 0.3s,
    visibility 0.3s !important;
}

.surely-table-wrapper:hover :deep(.surely-table-horizontal-scroll-hidden) {
  opacity: 0.6 !important;
  visibility: visible !important;
}

:deep(.surely-table-horizontal-scroll-viewport),
:deep(.surely-table-body-horizontal-scroll-container) {
  overflow-x: scroll !important;
  scrollbar-width: auto !important;
  scrollbar-color: var(--code-editor-comment) transparent !important;
}

:deep(.surely-table-horizontal-scroll-viewport)::-webkit-scrollbar,
:deep(.surely-table-body-horizontal-scroll-container)::-webkit-scrollbar {
  height: 5px !important;
  display: block !important;
}

:deep(.surely-table-horizontal-scroll-viewport)::-webkit-scrollbar-track,
:deep(.surely-table-body-horizontal-scroll-container)::-webkit-scrollbar-track {
  background: transparent !important;
  border-radius: 4px !important;
}

:deep(.surely-table-horizontal-scroll-viewport)::-webkit-scrollbar-thumb,
:deep(.surely-table-body-horizontal-scroll-container)::-webkit-scrollbar-thumb {
  background: var(--code-editor-comment) !important;
  border-radius: 4px !important;
}

:deep(.surely-table-vertical-scroll-viewport),
:deep(.surely-table-body-vertical-scroll-container) {
  overflow: auto !important;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

:deep(.surely-table-vertical-scroll-viewport)::-webkit-scrollbar,
:deep(.surely-table-body-vertical-scroll-container)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
  display: block !important;
}

:deep(.surely-table-vertical-scroll-viewport)::-webkit-scrollbar-thumb,
:deep(.surely-table-body-vertical-scroll-container)::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 4px;
}

:deep(.surely-table-vertical-scroll-viewport)::-webkit-scrollbar-thumb:hover,
:deep(
    .surely-table-body-vertical-scroll-container
  )::-webkit-scrollbar-thumb:hover {
  background: var(--white-opacity-20);
}
</style>